import React, { useEffect, useLayoutEffect, useState } from 'react';
import { HiOutlineBan } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { FaUserPlus } from 'react-icons/fa6';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Introduction, InviteType } from '../../../types/invites';
import useAuth from '../../../hooks/useAuth';
import { connectionService, invitationService } from '../../../services';
import { ProfileField } from '../../../types/profile';
import ResponsePageWrapper from '../../../components/layouts/ResponsePageWrapper';
import InvalidAuthBody from '../../../components/misc/InvalidAuthBody';
import { getInitials } from '../../../utils/stringUtils';
import UserDataForm from '../../../components/forms/userData/UserDataForm';
import useDeleteParams from '../../../hooks/useDeleteParams';
import NoDataSharedModal from '../../../components/modals/NoDataSharedModal';
import Button from '../../../components/misc/Button';
import ApiImage from '../../../components/misc/ApiImage';
import { SuccessPageState } from '../../../types/navigation';
import useMagicLogin from '../../../hooks/useMagicLogin';

export default function IntroductionsPage(): JSX.Element {
  const [params] = useSearchParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [introductions, setIntroductions] = useState<Introduction[]>(location.state?.introductions ?? undefined);
  const [selected, setSelected] = useState<string>();

  if (useMagicLogin()) {
    return <></>;
  }

  const id = params.get('id');
  useDeleteParams('id', 'link');

  useLayoutEffect(() => {
    if (id) setSelected(id);
  }, []);

  useEffect(() => {
    if (!auth) return;
    invitationService.getIntroductions().then((i): void => {
      if (!i.length) {
        navigate('/');
        return;
      }
      setIntroductions(i);
    });
  }, [JSON.stringify(auth)]);

  useEffect(() => {
    if (introductions && !introductions.length) {
      const state: SuccessPageState = {
        translationKey: 'page.common.success.message.introduction',
      };
      navigate('/success', { state });
    }
  }, [introductions?.length]);

  if (!auth)
    return (
      <ResponsePageWrapper>
        <InvalidAuthBody invitationId="" inviteType={InviteType.INTRODUCTION} />
      </ResponsePageWrapper>
    );

  return (
    <ResponsePageWrapper>
      {!selected ? (
        <IntroductionOverview
          introductions={introductions || []}
          setSelected={setSelected}
          setIntroductions={setIntroductions}
        />
      ) : (
        <AcceptIntroduction
          introduction={introductions?.find((i) => i.id === selected)!}
          introductions={introductions || []}
          setSelected={setSelected}
          setIntroductions={setIntroductions}
        />
      )}
    </ResponsePageWrapper>
  );
}

interface AcceptIntroductionProps {
  introduction: Introduction;
  introductions: Introduction[];
  setSelected: (id: string | undefined) => void;
  setIntroductions: (introductions: Introduction[]) => void;
}

function AcceptIntroduction({
  introduction,
  introductions,
  setSelected,
  setIntroductions,
}: AcceptIntroductionProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedData, setSelectedData] = useState<ProfileField[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const handleComplete = (): void => {
    if (selectedData.length === 0) setModalOpen(true);
    else acceptIntroduction();
  };

  const acceptIntroduction = async () => {
    await connectionService.acceptIntroduction(
      introduction.id,
      selectedData.map((f) => f.id!),
    );
    setIntroductions(introductions.filter((i) => i.id !== introduction.id));
    setSelected(undefined);
  };

  return (
    <>
      <div className="flex flex-col">
        <h2 className="text-lg font-medium">{t('page.introductions.selected.title')}</h2>
        <p className="text-sm">
          {t('page.introductions.selected.subtitle', {
            name: introduction.introducedAlias.replace('+', ' '),
          })}
        </p>
      </div>
      <UserDataForm selectedData={selectedData || []} setSelectedData={setSelectedData} hasSelectAll />
      <div className="mt-6 flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
        <Button className="underline" onClick={() => setSelected(undefined)}>
          {t('general.cancel')}
        </Button>
        <Button variant="primary" className="px-[32px]" onClick={handleComplete}>
          {t('page.shared.shareData')}
        </Button>
      </div>
      <NoDataSharedModal
        open={modalOpen}
        setOpen={setModalOpen}
        requesterName={introduction.introducedAlias.split('+')[0] || ''}
        handleComplete={acceptIntroduction}
      />
    </>
  );
}

interface IntroductionOverviewProps {
  introductions: Introduction[];
  setSelected: (id: string) => void;
  setIntroductions: (introductions: Introduction[]) => void;
}
function IntroductionOverview({
  introductions,
  setSelected,
  setIntroductions,
}: IntroductionOverviewProps): JSX.Element {
  const { t } = useTranslation();

  const declineIntroduction = async (introductionId: string) => {
    await connectionService.declineIntroduction(introductionId);
    setIntroductions(introductions.filter((i) => i.id !== introductionId));
  };

  return (
    <>
      <h1 className="text-2xl font-medium tracking-tight">{t('page.introductions.overview.title')}</h1>
      <p className="tracking-tight">{t('page.introductions.overview.subtitle')}</p>
      {!introductions.length && <p className="mt-2 text-sm">{t('page.introductions.overview.empty')}</p>}
      <div className="mt-4 flex max-h-[500px] w-full flex-1 flex-col divide-y divide-secondary-50 overflow-x-scroll rounded-3xl border-2 border-secondary-50">
        {introductions.map((i) => (
          <div className="flex w-full flex-col gap-1 px-2 py-4 text-sm">
            <div className="flex items-center gap-2">
              {i.introducerPicture ? (
                <ApiImage
                  src={i.introducerPicture}
                  alt={i.introducerAlias.replace('+', ' ')}
                  className="h-10 w-10 rounded-lg border border-secondary-50 bg-secondary"
                />
              ) : (
                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg border bg-secondary text-lg text-secondary-50">
                  {getInitials(i.introducerAlias)}
                </div>
              )}
              <p>
                <strong className="font-semibold">{i.introducerAlias.replace('+', ' ')}</strong>
                {t('page.introductions.overview.introduced')}
                <strong className="font-semibold">{i.introducedAlias.replace('+', ' ')}</strong>
              </p>
            </div>
            <div className="flex">
              <div className="mr-2 h-1 w-10" />
              <Button variant="primary" onClick={() => setSelected(i.id)}>
                <FaUserPlus className="h-4 w-4" />
                {t('general.accept')}
              </Button>
              <Button className="underline" onClick={() => declineIntroduction(i.id)}>
                <HiOutlineBan className="h-5 w-5" strokeWidth="2" />
                {t('general.decline')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
